import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Badge,
  Avatar,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Chip,
  InputBase,
  alpha,
  ListSubheader,
  Autocomplete,
  TextField,
  CircularProgress,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// Icons used by the global search dropdown to label result kinds. Most are
// already imported from the @mui/icons-material aggregate below; we only need
// the ones that aren't already in scope here.
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ViewKanban as ViewKanbanIcon,
  Assignment as AssignmentIcon,
  ChangeCircle as ChangeCircleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  CalendarMonth as CalendarMonthIcon,
  AccountBalance as AccountBalanceIcon,
  Home as HomeIcon,
  EventNote as EventNoteIcon,
  AttachMoney as AttachMoneyIcon,
  ManageAccounts as ManageAccountsIcon,
  Forum as ForumIcon,
  ChatBubble as ChatBubbleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Warehouse as WarehouseIcon,
  Timeline as TimelineIcon,
  Build as BuildIcon,
  ReceiptLong as ReceiptLongIcon,
  PeopleAlt as PeopleAltIcon,
  Contacts as ContactsIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  Apartment as ApartmentIcon,
  ConfirmationNumber as TicketIcon,
  MusicNote as MusicNoteIcon,
  MonitorHeart as MonitorHeartIcon,
  Tv as TvIcon,
  Description as DescriptionIcon,
  CompareArrows as CompareArrowsIcon,
  FactCheck as FactCheckIcon,
  Inventory2 as Inventory2Icon,
  Hub as HubIcon,
  Article as ArticleIcon,
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTicketDialog } from './TicketDialog';
import api, { getUnreadCount, getClientMessagesList, getChannels, getDemoMode, toggleDemoMode } from '../services/api';

// Global-search result kind → icon + chip color. Mirrors KIND_META in
// TicketDialog.js so visual treatment is consistent across the app.
const SEARCH_KIND_META = {
  client:     { icon: BusinessIcon,  color: 'primary',   label: 'Client'     },
  project:    { icon: WorkIcon,      color: 'secondary', label: 'Project'    },
  engagement: { icon: ApartmentIcon, color: 'info',      label: 'Engagement' },
  ticket:     { icon: TicketIcon,    color: 'warning',   label: 'Ticket'     },
  contact:    { icon: PersonIcon,    color: 'default',   label: 'Contact'    },
};

const drawerWidth = 260;

// ── Permission Groups (from Command Center Roles doc, April 2026) ──
// Executive Management: Robert Kaufman, Jonathan Flamm, Tom Clancy, Chris Georghiou, Pat Kross, Jeanne Ruvolo, Joe Calabrese
// Joe Calabrese (93) added 2026-07-30 per Tom — full Executive Management access
// (Jonathan asked Joe to review Command Center; he needs everything the ownership group sees).
const EXEC_MGMT_IDS = new Set([1, 2, 3, 4, 19, 90, 93]);
// Management: Engjell, Mike Grady, Mike Murray, Bill Hipp, Andrew Witanowski, Christine Cullinan, Mike Pittman
const MGMT_IDS = new Set([5, 6, 7, 8, 34, 43, 77]);
// Finance: Donna Canavan, Carmela Verni, Jeff Klein, Rosanne Squire, Eric Olivieri
const FINANCE_IDS = new Set([16, 17, 18, 20, 21]);
// Sales: Sales team reps + those with Sales role
const SALES_ROLES = new Set(['Sales']);
// Proposal Review extras (Tom Clancy 2026-05-22): Dawn Halpin + Silvia Azucar.
// This is a per-tab override; it does NOT grant access to any other finance pages.
const PROPOSAL_REVIEW_EXTRA_IDS = new Set([56, 58]);

// Sage Recon (Tom Clancy 2026-06-10): side-by-side reconciliation of
// Command Center vs live Sage 300 numbers, with the exact Sage query
// alongside each tile so Jeanne (and anyone else) can verify in Sage.
// Locked to Robert (1), Jonathan (2), Tom (3), Chris G (4), Pat (19), Jeanne (90), Joe Calabrese (93).
// Backend route enforces the same list — keep these in sync with
// `ALLOWED_IDS` in server/routes/sage-recon.js.
const SAGE_RECON_ALLOWED_IDS = new Set([1, 2, 3, 4, 19, 90, 93]);
// New Sales by Salesperson — narrow allowlist mirroring the backend route.
// Tom (3) + Robert Kaufman (1) + Jonathan Flamm (2) + Chris Georghiou (4).
// Tom Clancy 2026-06-17. Keep in sync with App.js SALES_REPORT_ALLOWED_IDS
// and server/routes/sales-by-salesperson.js ALLOWED_USER_IDS.
const SALES_REPORT_ALLOWED_IDS = new Set([3, 1, 2, 4]);

// Color coding for sidebar categories (legacy — used by some downstream code)
const COLOR_FINANCE = '#1565C0';  // Blue — finance/management items
const COLOR_FIELD = '#2E7D32';    // Green — field operations items
const COLOR_PREVIEW = '#F57C00';  // Orange — preview/unreleased items

// Tom Clancy, 2026-04-25: vibrant section-based sidebar.
// Each nav item is tagged with `section` (a finer grouping than the old
// `category` field). A section maps to a named, color-coded block in the
// sidebar with its own accent bar, icon-tint color, and active-state hue.
//
// Section keys are referenced in getVisibleNavItems and SECTION_SPEC.
const SECTION_SPEC = {
  everyday:      { label: 'EVERY DAY',         color: '#1A5490', accent: '#1a6bc4', tint: 0.10 },
  work:          { label: 'WORK',              color: '#2E7D32', accent: '#43a047', tint: 0.10 },
  service:       { label: 'AFTERCARE',         color: '#E65100', accent: '#fb8c00', tint: 0.10 },
  clients:       { label: 'CLIENTS & SALES',   color: '#6A1B9A', accent: '#8e24aa', tint: 0.10 },
  finance:       { label: 'FINANCE & MGMT',    color: '#1565C0', accent: '#1e88e5', tint: 0.10 },
  communication: { label: 'COMMUNICATION',     color: '#00838F', accent: '#00acc1', tint: 0.10 },
  preview:       { label: 'PREVIEW',           color: '#F57C00', accent: '#fb8c00', tint: 0.10 },
  tools:         { label: 'TOOLS',             color: '#5E35B1', accent: '#7e57c2', tint: 0.10 },
  admin_tools:   { label: 'ADMIN TOOLS',       color: '#455A64', accent: '#607d8b', tint: 0.10 },
};

const SECTION_ORDER = ['everyday', 'work', 'service', 'communication', 'clients', 'finance', 'tools', 'preview', 'admin_tools'];

/**
 * Determine permission groups for a user.
 * Returns an object with boolean flags for each group.
 */
const getUserGroups = (user) => {
  const id = user?.id;
  const role = user?.role || '';
  const roles = user?.roles || [];
  const rolesSet = new Set(roles);

  const isExecMgmt = EXEC_MGMT_IDS.has(id);
  const isMgmt = MGMT_IDS.has(id) || role === 'Management';
  const isFinance = FINANCE_IDS.has(id) || role === 'Finance';
  const isSales = SALES_ROLES.has(role) || rolesSet.has('Sales');
  // Field Operations = PMs + Technicians + IT + Engineering + Programmers + CAD + AfterCare + Shades + Lighting + Warehouse + Drivers + Proposal
  const fieldRoles = new Set([
    'PM', 'Field Operations', 'Field Operations Manager',
    'Installation Technician', 'Lead Tech',
    'IT', 'Engineering', 'CAD Operator', 'CAD Manager',
    'Programming', 'Production Manager',
    'AfterCare', 'AfterCare Technician', 'Service Tech',
    'Shade Design & Sales Manager', 'Shade Manager', 'Shade Tech', 'Shade Field Manager',
    'Lighting Manager', 'Lighting Tech',
    'Warehouse', 'Driver',
    'Proposal Admin', 'Purchasing',
  ]);
  const isFieldOps = fieldRoles.has(role);

  const isSageRecon = SAGE_RECON_ALLOWED_IDS.has(id);
  const isSalesReportViewer = SALES_REPORT_ALLOWED_IDS.has(id);

  // Owner gate — Tom Clancy only (id 3). Used to hide owner-only views
  // (e.g. /admin/login-activity) from everyone else, including other
  // Management users. The matching backend route also locks to id 3,
  // so this is just a UI hint, not the security boundary.
  const isOwner = id === 3;

  return {
    isOwner,
    isSalesReportViewer,
    isSageRecon,
    isExecMgmt,
    isMgmt: isExecMgmt || isMgmt, // Exec Management implies Management access
    isFinance,
    isSales,
    isFieldOps,
    isPM: role === 'PM' || role === 'Field Operations Manager' || rolesSet.has('PM'),
    isAfterCare: role === 'AfterCare' || role === 'AfterCare Technician',
    isShade: role === 'Shade Design & Sales Manager' || role === 'Shade Manager' || role === 'Shade Field Manager' || role === 'Shade Tech',
    // CAD role — covers CAD Manager, CAD Supervisor, CAD Operator titles/roles.
    // Used to expose the CAD Dropbox Master Import tool. Wilmer + team.
    isCAD: /cad/i.test(role) || /cad/i.test(user?.title || ''),
    isAny: true, // Everyone
  };
};

/**
 * Returns the ordered sidebar nav items matching Tom's Command Center spec (1-18).
 * Items are tagged with a `category` for color coding:
 *   'finance' = blue dot  (Finance/Management scope)
 *   'field'   = green dot (Field Operations scope)
 *   'preview' = orange dot (Preview/unreleased)
 *   null      = no dot (everyone / general)
 */
const getVisibleNavItems = (user) => {
  const g = getUserGroups(user);
  const items = [];

  // EVERY DAY — the things everyone opens first thing in the morning.
  items.push({ text: 'Ask ACS',           icon: <AutoAwesomeIcon />,     path: '/ask',          section: 'everyday', highlight: true });
  // Tom Clancy 2026-10-23: /dispatch is now the new DispatchPreview-based
  // calendar. The old Google Doc–backed calendar lives at /dispatch-legacy
  // for a one-week revert window (not surfaced in the sidebar). Dispatch
  // emails stay in dry-run mode until explicitly flipped live.
  items.push({ text: 'Dispatch Calendar', icon: <EventNoteIcon />,       path: '/dispatch',     section: 'everyday' });
  // Billing Queue (preview) — restricted to Eric, Pat, Chris, Jeanne, Tom, Grady, Hibney
  items.push({ text: 'Billing Queue', icon: <AttachMoneyIcon />, path: '/billing-queue', section: 'preview' });
  // Active Projects Calendar (Tom Clancy 2026-08-05) — company-wide
  // day/week/month calendar of the Active Projects list. Reads Pre-Final
  // and Final windows that PMs enter on /wip/active.
  items.push({ text: 'Projects Calendar', icon: <CalendarMonthIcon />,   path: '/wip/calendar', section: 'everyday' });
  items.push({ text: 'My Tasks',          icon: <AssignmentIcon />,      path: '/tasks',        section: 'everyday' });
  items.push({ text: 'Daily Report',      icon: <EventNoteIcon />,       path: '/daily-report', section: 'everyday' });
  if (g.isExecMgmt) {
    items.push({ text: 'Executive Dashboard', icon: <DashboardIcon />,   path: '/',             section: 'everyday' });
  }

  // WORK — jobs, projects, schedule.
  items.push({ text: 'WIP',               icon: <ViewKanbanIcon />,      path: '/projects',          section: 'work' });
  // Active Projects (Tom Clancy 2026-08-05) — curated view of the ~197
  // jobs the field team is actually working on right now (vs. the ~316
  // Sage still marks 'In progress'). Landing spot for PMs to enter
  // Pre-Final / Final start/end dates.
  items.push({ text: 'Active Projects',   icon: <ViewKanbanIcon />,      path: '/wip/active',        section: 'work' });
  if (process.env.REACT_APP_ENGAGEMENT_UI === 'true') {
    // Tom Clancy (2026-08-06): graduated out of beta — this is the
    // client + site view of WIP active projects.
    items.push({ text: 'Projects',        icon: <ApartmentIcon />,       path: '/engagements',       section: 'work' });
  }
  items.push({ text: 'Project Timeline',  icon: <CalendarMonthIcon />,   path: '/calendar',          section: 'work' });
  items.push({ text: 'Company Timeline',  icon: <TimelineIcon />,        path: '/company-timeline',  section: 'work' });
  items.push({ text: 'Warehouse',         icon: <WarehouseIcon />,       path: '/warehouse',         section: 'work' });
  // Product Search — search every D-Tools line item across every project.
  // Tom Clancy 2026-06-19: 'outside of inventory and warehouse, I need to
  // search any product sold on any project, get a running total, see which
  // jobs.'
  items.push({ text: 'Product Search',    icon: <Inventory2Icon />,      path: '/product-search',    section: 'work' });

  // AFTERCARE — service operations.
  items.push({ text: 'AfterCare',         icon: <BuildIcon />,           path: '/aftercare',         section: 'service' });
  // NOC subcategories under AfterCare (Tom Clancy, 2026-05-26). External
  // reverse-proxy paths served by Caddy at /noc/* — full page navigation,
  // not React Router routes.
  items.push({ text: 'NOC Dashboard',     icon: <MonitorHeartIcon />,    path: '/noc/dashboard',     section: 'service', parent: 'AfterCare', external: true });
  items.push({ text: 'NOC TV View',       icon: <TvIcon />,              path: '/noc/tv',            section: 'service', parent: 'AfterCare', external: true });
  items.push({ text: 'NOC Reports',       icon: <DescriptionIcon />,     path: '/noc/reports',       section: 'service', parent: 'AfterCare', external: true });
  items.push({ text: 'Tickets',           icon: <TicketIcon />,          path: '/tickets',           section: 'service', showTicketBadge: true });

  // COMMUNICATION — messages and the Wire.
  items.push({ text: 'The Wire',          icon: <ChatBubbleIcon />,      path: '/messages',          section: 'communication', showInternalMsgBadge: true });

  // TOOLS — fun + utility apps available to everyone.
  items.push({ text: 'Soundcheck',        icon: <MusicNoteIcon />,       path: '/soundcheck',        section: 'tools' });
  // Archived Dropbox Links — searchable historical lookup for closed/old
  // jobs that no longer have a project record. Open to all authenticated
  // users (backend route is auth-only, no role gate). Wilmer Rengifo
  // 2026-10-07: not surfaced in global Search, so give it a sidebar entry.
  items.push({ text: 'Archived Dropbox Links', icon: <FolderOpenIcon />, path: '/tools/archived-dropbox-links.html', section: 'tools', external: true });

  // CLIENTS & SALES — anything client-facing.
  // Weekly Activity — Sales reps fill out their own form; Mgmt sees consolidated.
  // Replaces the Zoho form (retired with Zoho One). Tom Clancy 2026-06-10.
  if (g.isSales || g.isMgmt) {
    items.push({ text: 'Weekly Activity',  icon: <FactCheckIcon />,       path: '/weekly-activity',    section: 'clients' });
  }
  items.push({ text: 'CRM',               icon: <ContactsIcon />,        path: '/crm',               section: 'clients' });
  // D-Tools-backed CRM — Tom 2026-06-19: sunsetting Zoho, CRM data now sourced from D-Tools.
  items.push({ text: 'CRM (D-Tools)',      icon: <HubIcon />,             path: '/dtools-crm',        section: 'clients' });
  items.push({ text: 'ACS Directory',     icon: <ContactsIcon />,        path: '/directory',         section: 'clients' });
  items.push({ text: 'Field Guides',      icon: <MenuBookIcon />,        path: '/field-guides',      section: 'work' });
  // Sales Pipeline: visible to Mgmt + Sales + Finance + all PMs (PMs see
  // read-only). Tom Clancy 2026-05-14.
  if (g.isMgmt || g.isSales || g.isFinance || g.isPM) {
    items.push({ text: 'Sales Pipeline',  icon: <TrendingUpIcon />,      path: '/sales-pipeline',    section: 'clients' });
  }

  // FINANCE & MGMT — money, capacity, oversight. Permission-gated per item.
  if (g.isMgmt || g.isFinance || g.isPM) {
    items.push({ text: 'Labor Utilization', icon: <PeopleAltIcon />,     path: '/labor-utilization', section: 'finance' });
    items.push({ text: 'Labor Variance',    icon: <BarChartIcon />,      path: '/labor-variance',    section: 'finance' });
  }
  // IT Team Utilization: executives + finance + Mike Murray (Director of IT, id 7).
  // Added 2026-10-01 per Mike's request (cost-code-based IT utilization view).
  if (g.isMgmt || g.isFinance || user?.role === 'IT') {
    items.push({ text: 'IT Team Utilization', icon: <PeopleAltIcon />,  path: '/it-team-utilization', section: 'finance' });
  }
  if (g.isMgmt || g.isFinance || g.isPM || g.isSales) {
    items.push({ text: 'A/R Report',      icon: <AccountBalanceIcon />,  path: '/ar-report',         section: 'finance' });
    // 'A/R Collected' removed from sidebar 2026-06-17 — now accessed via the
    // 'Collected This Month' card on the A/R Report page (finance team request).
    // Route + page kept intact; no data loss.
    items.push({ text: 'PO Lookup',       icon: <ReceiptLongIcon />,     path: '/purchase-orders',   section: 'finance' });
  }
  if (g.isMgmt || g.isFinance || g.isSales) {
    items.push({ text: 'New Business',    icon: <TrendingUpIcon />,      path: '/new-business',      section: 'finance' });
  }
  // Proposal Review: Mgmt + Finance + Sales, plus Dawn Halpin and Silvia Azucar
  // (Proposal Admin role) — Tom 2026-05-22. Per-tab override, no other finance access.
  if (g.isMgmt || g.isFinance || g.isSales || PROPOSAL_REVIEW_EXTRA_IDS.has(user?.id)) {
    items.push({ text: 'Proposal Review', icon: <ReceiptLongIcon />,     path: '/proposal-review',   section: 'finance' });
  }
  if (g.isMgmt || g.isFinance) {
    // 'MTD Reconciler' removed from sidebar 2026-06-17 — moved to the
    // A/R Report page (card next to 'Collected This Month'). Route + page
    // kept intact; no data loss.
    items.push({ text: 'Client Hours Recording', icon: <ReceiptLongIcon />, path: '/client-hours-recording', section: 'finance' });
    items.push({ text: 'Closed Jobs',     icon: <AccountBalanceIcon />,  path: '/closed-jobs',       section: 'finance' });
  }
  if (g.isExecMgmt) {
    items.push({ text: 'Mfr Purchases',   icon: <BarChartIcon />,        path: '/manufacturer-purchases', section: 'finance' });
  }
  // Sage Recon — verify CC numbers against live Sage. 6 users only.
  if (g.isSageRecon) {
    items.push({ text: 'Sage Recon',      icon: <CompareArrowsIcon />,   path: '/sage-recon',             section: 'finance' });
  }
  // Owner-only: login activity dashboard (Tom id 3 only).
  if (g.isOwner) {
    items.push({ text: 'Login Activity',  icon: <PeopleAltIcon />,       path: '/admin/login-activity',   section: 'finance' });
  }
  // New Sales by Salesperson — narrow allowlist (Tom + Robert + Jonathan +
  // Chris). Backend matches the same id set. Tom 2026-06-17.
  if (g.isSalesReportViewer) {
    items.push({ text: 'New Sales by Salesperson', icon: <TrendingUpIcon />, path: '/admin/new-sales-by-salesperson', section: 'finance' });
  }
  // PM Workload — active projects/jobs per PM with discipline breakdown,
  // dollar value, and project status. Tom 2026-06-18.
  if (g.isMgmt || g.isFinance || g.isOwner) {
    items.push({ text: 'PM Workload', icon: <ManageAccountsIcon />, path: '/admin/pm-workload', section: 'finance' });
  }

  // Client Executive Summary — Sales tool, gated to Mgmt + Finance + Sales
  // + PMs + Dawn/Silvia (Proposal Review allowlist). Moved out of PREVIEW
  // into CLIENTS & SALES — Tom 2026-06-30. PMs added 2026-08-14 so they can
  // generate exec summaries for their own projects.
  if (g.isMgmt || g.isFinance || g.isSales || g.isPM || PROPOSAL_REVIEW_EXTRA_IDS.has(user?.id)) {
    items.push({ text: 'Client Executive Summary', icon: <ArticleIcon />, path: '/exec-summary', section: 'clients' });
  }

  // PREVIEW — unreleased / experimental.
  if (g.isMgmt || g.isFinance || g.isPM || g.isSales) {
    items.push({ text: 'Client Messages',        icon: <ForumIcon />,      path: '/client-messages',         section: 'preview', showMsgBadge: true });
    items.push({ text: 'Client Portal Preview',  icon: <VisibilityIcon />, path: '/client-portal-preview',   section: 'preview' });
  }

  // ── ADMIN TOOLS (bottom of sidebar) ──────────────────────────────────────
  // CAD Dropbox Master Import — Wilmer + exec/software mgmt. Opens the
  // standalone /tools/cad-import.html page in a new tab; the server-side
  // route enforces its own allowlist (ids 3, 5, 6, 12 + Management/Owner/CAD).
  if (g.isCAD || g.isExecMgmt || g.isMgmt) {
    items.push({
      text: 'Dropbox Upload',
      icon: <CloudUploadIcon />,
      path: '/tools/cad-import.html',
      section: 'admin_tools',
      external: true,
    });
  }

  return items;
};

// Category color dot for sidebar items
const getCategoryColor = (category) => {
  switch (category) {
    case 'finance': return COLOR_FINANCE;
    case 'field': return COLOR_FIELD;
    case 'preview': return COLOR_PREVIEW;
    default: return null;
  }
};

const getCategoryTooltip = (category) => {
  switch (category) {
    case 'finance': return 'Finance & Management';
    case 'field': return 'Field Operations';
    case 'preview': return 'Preview';
    default: return null;
  }
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Collapsible sidebar sections (Tom Clancy 2026-08-05). Persist in
  // localStorage so each user's collapsed state survives navigation and
  // browser reloads. Default: everything expanded.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem('sidebar_collapsed_sections');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('sidebar_collapsed_sections', JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [internalUnreadCount, setInternalUnreadCount] = useState(0);
  const [ticketBadgeCount, setTicketBadgeCount] = useState(0);
  // Universal search bar (Mike Murray, 2026-04-24): one search bar across the
  // whole app, hits /api/search/global, results are clickable and route to the
  // matching page (project / client / engagement / ticket / contact).
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchOpts, setGlobalSearchOpts] = useState([]);
  const [globalSearching, setGlobalSearching] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const { user, logout } = useAuth();
  const { openTicketDialog } = useTicketDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const isMgmt = user?.role === 'Management' || user?.roles?.includes('Management');
  const visibleNavItems = getVisibleNavItems(user);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Poll for unread client messages (management only)
  // Load demo mode status for management
  useEffect(() => {
    if (!isMgmt) return;
    getDemoMode().then(r => setDemoMode(r.data.demo_mode)).catch(() => {});
  }, [isMgmt]);

  const handleDemoToggle = async () => {
    try {
      const r = await toggleDemoMode();
      setDemoMode(r.data.demo_mode);
      window.location.reload(); // Refresh to re-fetch all data with new mode
    } catch (e) {
      console.error('Demo toggle error:', e);
    }
  };

  useEffect(() => {
    if (!isMgmt) return;
    const loadMsgCount = async () => {
      try {
        const res = await getClientMessagesList();
        const total = (res.data.threads || []).reduce((sum, t) => sum + (t.unread_count || 0), 0);
        setUnreadMsgCount(total);
      } catch (e) {
        // silently ignore
      }
    };
    loadMsgCount();
    const interval = setInterval(loadMsgCount, 60000); // Poll every 60 seconds
    return () => clearInterval(interval);
  }, [isMgmt]);

  // Poll for internal messaging unread count (all users)
  useEffect(() => {
    const loadInternalMsgCount = async () => {
      try {
        const res = await getChannels();
        const total = (res.data || []).reduce((sum, c) => sum + (parseInt(c.unread_count) || 0), 0);
        setInternalUnreadCount(total);
      } catch (e) {
        // silently ignore — user may not be in any channels yet
      }
    };
    loadInternalMsgCount();
    const interval = setInterval(loadInternalMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Poll ticket badge count — shows count of OPEN tickets assigned to me.
  useEffect(() => {
    const loadTicketBadge = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/tickets/stats/summary', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setTicketBadgeCount(parseInt(data?.mine_open || 0, 10));
      } catch (e) { /* ignore */ }
    };
    loadTicketBadge();
    const interval = setInterval(loadTicketBadge, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  // Debounced live search against /api/search/global. Tom Clancy / Mike Murray
  // (2026-04-24): make this the single search bar for the whole company.
  useEffect(() => {
    const q = (globalSearch || '').trim();
    if (q.length < 2) { setGlobalSearchOpts([]); return; }
    let cancelled = false;
    const h = setTimeout(() => {
      setGlobalSearching(true);
      api.get(`/search/global?q=${encodeURIComponent(q)}`)
        .then(r => {
          if (cancelled) return;
          setGlobalSearchOpts(Array.isArray(r.data?.results) ? r.data.results : []);
        })
        .catch(() => { if (!cancelled) setGlobalSearchOpts([]); })
        .finally(() => { if (!cancelled) setGlobalSearching(false); });
    }, 180);
    return () => { cancelled = true; clearTimeout(h); };
  }, [globalSearch]);

  // Pick a result → navigate to its page. Prefer an explicit href from the
  // server (consistent linking), fall back to a kind-based router for safety.
  const handlePickGlobalSearchOption = (opt) => {
    if (!opt) return;
    let target = opt.href;
    if (!target) {
      // Fallback router — mirrors server-side href defaults. Tom Clancy /
      // Mike Murray (2026-04-24): clients and engagements navigate-and-filter
      // Projects rather than going to a deep detail page.
      switch (opt.kind) {
        case 'project':
          target = `/projects/${opt.project_id || opt.id}`;
          break;
        case 'engagement': {
          const id = opt.engagement_id || opt.id;
          const label = opt.label || opt.client_name || `#${id}`;
          target = `/projects?engagement_id=${id}&engagement_label=${encodeURIComponent(label)}`;
          break;
        }
        case 'client': {
          const ar = opt.ar_code || opt.id || '';
          const label = opt.client_name || opt.label || ar;
          target = `/projects?ar=${encodeURIComponent(ar)}&ar_label=${encodeURIComponent(label)}`;
          break;
        }
        case 'ticket':     target = `/tickets?open=${opt.ticket_id || opt.id}`; break;
        case 'contact':    target = `/crm?contact=${encodeURIComponent(opt.contact_zoho_id || opt.id || '')}`; break;
        default:           target = '/';
      }
    }
    navigate(target);
    setGlobalSearch('');
    setGlobalSearchOpts([]);
    setGlobalSearchOpen(false);
  };

  // Enter without selecting a row → fall back to the first result, or to the
  // legacy /projects?search=... view (so a query like "123 Main St" still goes
  // somewhere even if Autocomplete didn't highlight a row first).
  const doGlobalSearch = () => {
    if (!globalSearch.trim()) return;
    if (globalSearchOpts.length > 0) {
      handlePickGlobalSearchOption(globalSearchOpts[0]);
      return;
    }
    navigate(`/projects?search=${encodeURIComponent(globalSearch.trim())}`);
    setGlobalSearch('');
  };

  // Tom Clancy, 2026-04-25 — vibrant, sectioned, color-coded sidebar.
  // Group items by `section` (legacy items without a section default to 'work').
  //
  // STANDING RULE (Tom Clancy, 2026-05-11): every section's items are sorted
  // alphabetically by label. Items flagged `highlight: true` (e.g. "Ask ACS")
  // stay pinned at the top of their section; everything else is A→Z.
  // Children (items with a `parent` field) are kept attached to their parent
  // rather than alphabetized at the section level. They render indented,
  // sorted alphabetically among siblings, immediately after the parent item.
  const itemsBySection = SECTION_ORDER.reduce((acc, key) => {
    const inSection = visibleNavItems.filter(i => (i.section || 'work') === key);
    const children  = inSection.filter(i => i.parent);
    const topLevel  = inSection.filter(i => !i.parent);
    const pinned    = topLevel.filter(i => i.highlight);
    const rest      = topLevel
      .filter(i => !i.highlight)
      .sort((a, b) => a.text.localeCompare(b.text, 'en', { sensitivity: 'base' }));
    const ordered = [];
    for (const item of [...pinned, ...rest]) {
      ordered.push(item);
      const kids = children
        .filter(c => c.parent === item.text)
        .sort((a, b) => a.text.localeCompare(b.text, 'en', { sensitivity: 'base' }));
      ordered.push(...kids);
    }
    acc[key] = ordered;
    return acc;
  }, {});

  const renderNavItem = (item, section) => {
    const sectionSpec = SECTION_SPEC[section] || SECTION_SPEC.work;
    const accent = sectionSpec.accent;
    const isSelected = location.pathname === item.path;

    // The hero "Ask ACS" item keeps its signature gradient.
    const isHero = !!item.highlight;
    // Child items (e.g. NOC entries under AfterCare) are indented to visually
    // signal nesting under their parent.
    const isChild = !!item.parent;

    const handleClick = () => {
      if (item.external) {
        // Open reverse-proxied paths (e.g. /noc/*) in a new tab so the
        // user doesn't lose their place in the Command Center.
        window.open(item.path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.path);
      }
    };

    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
        <ListItemButton
          onClick={handleClick}
          sx={{
            mx: 1,
            ml: isChild ? 3 : 1,
            borderRadius: 1.5,
            py: isChild ? 0.4 : 0.6,
            transition: 'all 0.15s ease',
            ...(isHero && {
              background: `linear-gradient(135deg, ${sectionSpec.color} 0%, ${accent} 100%)`,
              color: '#fff',
              '&:hover': { background: `linear-gradient(135deg, ${sectionSpec.color} 0%, ${accent} 100%)`, filter: 'brightness(1.1)' },
            }),
            ...(!isHero && isSelected && {
              background: `linear-gradient(90deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.05)} 100%)`,
              borderLeft: `3px solid ${accent}`,
              pl: 1.25,
              '&:hover': { background: `linear-gradient(90deg, ${alpha(accent, 0.24)} 0%, ${alpha(accent, 0.08)} 100%)` },
            }),
            ...(!isHero && !isSelected && {
              '&:hover': { background: alpha(accent, 0.08) },
            }),
          }}
        >
          {/* Tinted icon container — the visual change that makes the sidebar pop. */}
          <Box
            sx={{
              minWidth: 32,
              height: 28,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isHero ? 'rgba(255,255,255,0.18)' : (isSelected ? alpha(accent, 0.20) : alpha(accent, 0.10)),
              color: isHero ? '#fff' : accent,
              mr: 1.25,
              flexShrink: 0,
              transition: 'all 0.15s ease',
            }}
          >
            {item.showMsgBadge ? (
              <Badge badgeContent={unreadMsgCount || 0} color="error">{item.icon}</Badge>
            ) : item.showInternalMsgBadge ? (
              <Badge badgeContent={internalUnreadCount || 0} color="error">{item.icon}</Badge>
            ) : item.showTicketBadge ? (
              <Badge badgeContent={ticketBadgeCount || 0} color="error">{item.icon}</Badge>
            ) : item.icon}
          </Box>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span style={{
                  fontWeight: isHero ? 700 : (isSelected ? 600 : 500),
                  fontSize: isHero ? '0.92rem' : '0.85rem',
                  letterSpacing: isHero ? 0.2 : 0,
                }}>
                  {item.text}
                </span>
                {item.beta && (
                  <Chip label="BETA" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: alpha(accent, 0.15), color: accent, fontWeight: 700 }} />
                )}
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSection = (sectionKey) => {
    const sectionItems = itemsBySection[sectionKey] || [];
    if (sectionItems.length === 0) return null;
    const spec = SECTION_SPEC[sectionKey];
    const collapsed = collapsedSections.has(sectionKey);
    return (
      <Box key={sectionKey} sx={{ mb: 1.25 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => toggleSection(sectionKey)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(sectionKey); } }}
          sx={{
            px: 2, pt: 1.25, pb: 0.5,
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: alpha(spec.accent, 0.06) },
          }}
        >
          <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: spec.accent }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.65rem',
              letterSpacing: 1.4,
              color: spec.color,
              textTransform: 'uppercase',
              flex: 1,
            }}
          >
            {spec.label}
          </Typography>
          <Typography variant="caption" sx={{ color: spec.color, opacity: 0.6, fontSize: '0.65rem' }}>
            {sectionItems.length}
          </Typography>
          {collapsed
            ? <ExpandMoreIcon sx={{ fontSize: 16, color: spec.color, opacity: 0.7 }} />
            : <ExpandLessIcon sx={{ fontSize: 16, color: spec.color, opacity: 0.7 }} />
          }
        </Box>
        <Collapse in={!collapsed} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {sectionItems.map(item => renderNavItem(item, sectionKey))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ bgcolor: '#0D2C54', color: 'white', cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }} onClick={() => navigate('/')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/img/acs-nav-logo-white.png" alt="ACS" style={{ height: 36 }} onError={(e) => { e.target.style.display = 'none'; }} />
          <Box>
            <Typography variant="subtitle1" noWrap component="div" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
              ACS
            </Typography>
            <Typography variant="caption" noWrap component="div" sx={{ lineHeight: 1.1, opacity: 0.85 }}>
              Command Center
            </Typography>
          </Box>
        </Box>
      </Toolbar>

      {/* Quick "+ New Ticket" — one click from anywhere in the app. */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <ListItemButton
          onClick={() => openTicketDialog()}
          sx={{
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)',
            color: '#fff',
            boxShadow: '0 2px 6px rgba(198,40,40,0.25)',
            '&:hover': { background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)', boxShadow: '0 3px 10px rgba(198,40,40,0.35)' },
            py: 0.75,
          }}
        >
          <Box sx={{ minWidth: 32, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.2)', mr: 1.25 }}>
            <TicketIcon fontSize="small" />
          </Box>
          <ListItemText
            primary="+ New Ticket"
            primaryTypographyProps={{ sx: { fontWeight: 700, fontSize: '0.88rem', letterSpacing: 0.2 } }}
          />
        </ListItemButton>
      </Box>
      <Divider sx={{ mb: 0.5 }} />

      {/* Sectioned, color-coded nav. */}
      {SECTION_ORDER.map(renderSection)}

      <Divider sx={{ mt: 1 }} />
      <List dense>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/settings')}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
        {isMgmt && (
          <ListItem disablePadding>
            <ListItemButton onClick={handleDemoToggle}>
              <ListItemIcon>
                <SettingsIcon sx={{ color: demoMode ? '#e65100' : 'inherit' }} />
              </ListItemIcon>
              <ListItemText
                primary={demoMode ? '● DEMO MODE' : 'Demo Mode'}
                primaryTypographyProps={{
                  sx: {
                    color: demoMode ? '#e65100' : 'inherit',
                    fontWeight: demoMode ? 700 : 400,
                    fontSize: '0.85rem',
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Universal company-wide search bar. */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 1,
              bgcolor: alpha('#000', 0.05),
              '&:hover': { bgcolor: alpha('#000', 0.08) },
              mr: 2,
              ml: 0,
              width: { xs: '100%', sm: 'auto' },
              flexGrow: 1,
              maxWidth: 520,
            }}
          >
            <Autocomplete
              size="small"
              freeSolo
              fullWidth
              /* Only open the dropdown once the user has typed >=2 chars.
                 We still let MUI manage onOpen/onClose internally so the
                 listbox behaves naturally on blur, escape, selection, etc. */
              open={globalSearchOpen && globalSearch.trim().length >= 2}
              onOpen={() => setGlobalSearchOpen(true)}
              onClose={() => setGlobalSearchOpen(false)}
              options={globalSearchOpts}
              loading={globalSearching}
              filterOptions={(x) => x}
              inputValue={globalSearch}
              onInputChange={(_, v, reason) => {
                if (reason === 'reset') return; // ignore reset events from selection
                setGlobalSearch(v);
              }}
              onChange={(_, v) => {
                if (v && typeof v === 'object') handlePickGlobalSearchOption(v);
              }}
              getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt?.label || '')}
              isOptionEqualToValue={(a, b) => a?.kind === b?.kind && a?.id === b?.id}
              groupBy={(opt) => (SEARCH_KIND_META[opt.kind] || SEARCH_KIND_META.project).label}
              autoHighlight
              noOptionsText={(globalSearch || '').trim().length < 2 ? 'Type at least 2 characters…' : 'No matches'}
              slotProps={{ paper: { sx: { minWidth: 480 } } }}
              renderOption={(props, opt) => {
                const meta = SEARCH_KIND_META[opt.kind] || SEARCH_KIND_META.project;
                const Icon = meta.icon;
                // Tom Clancy (2026-08-06): the site address must be the
                // most identifiable thing when hovering any result. Render
                // it on its own line, bold/near-primary color, directly
                // under the client/project name. Other meta (job #, AR,
                // contacts) go on a third, quieter line.
                const addr = opt.site_address
                  || (Array.isArray(opt.site_addresses) && opt.site_addresses[0])
                  || null;
                const extraSites = Array.isArray(opt.site_addresses)
                  ? Math.max(0, opt.site_addresses.length - 1)
                  : 0;
                const addrLine = addr
                  ? (extraSites > 0 ? `${addr}  (+${extraSites} more site${extraSites === 1 ? '' : 's'})` : addr)
                  : null;
                // Build a metaLine that excludes the address (since we're
                // now rendering the address on its own line). We fall
                // back to opt.sublabel when we can't reconstruct it.
                let metaLine = null;
                if (opt.kind === 'client') {
                  const activeCount = Number(opt.active_project_count) || 0;
                  const wipTag = activeCount > 0
                    ? `In WIP · ${activeCount} active project${activeCount === 1 ? '' : 's'}`
                    : 'No active WIP projects';
                  metaLine = [
                    wipTag,
                    opt.ar_code ? `AR ${opt.ar_code}` : null,
                    opt.contact_name || null,
                    opt.contact_email || null,
                  ].filter(Boolean).join(' • ');
                } else if (opt.kind === 'project') {
                  metaLine = [
                    opt.job_number,
                    opt.client_name,
                    opt.ar_code,
                    opt.matched_alias_value && opt.matched_alias_type
                      ? `also known as: “${opt.matched_alias_value}”`
                      : null,
                  ].filter(Boolean).join(' • ');
                } else if (opt.kind === 'engagement') {
                  metaLine = opt.sublabel && addrLine
                    ? opt.sublabel.split(' • ').filter(s => s && s.trim() !== addr).join(' • ')
                    : opt.sublabel;
                } else {
                  metaLine = opt.sublabel;
                }
                return (
                  <Box component="li" {...props} key={`${opt.kind}-${opt.id}`}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, width: '100%', py: 0.5 }}>
                      <Icon fontSize="small" color={meta.color === 'default' ? 'action' : meta.color} sx={{ mt: 0.25 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{opt.label}</Typography>
                        {addrLine && (
                          <Typography
                            variant="body2"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              color: 'text.primary',
                              mt: 0.25,
                              whiteSpace: 'normal',
                              lineHeight: 1.25,
                            }}
                            title={addr}
                          >
                            📍 {addrLine}
                          </Typography>
                        )}
                        {metaLine && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                            {metaLine}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={meta.label}
                        color={meta.color === 'default' ? undefined : meta.color}
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, mt: 0.25 }}
                      />
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => {
                // MUI v9 Autocomplete: params.InputProps holds the InputBase
                // props (ref, className, startAdornment, endAdornment, etc).
                // params.inputProps (lowercase) holds the <input> props
                // (value, onChange, onBlur, onFocus, ref, aria-*).
                // We MUST pass both through unchanged; previously we used
                // slotProps.input which silently dropped the controlled
                // value/onChange wiring, so typing never updated state.
                const { InputProps = {}, inputProps = {}, ...rest } = params;
                return (
                  <TextField
                    {...rest}
                    inputProps={inputProps}
                    placeholder="Search anything — client, project, job #, address, contact, ticket…"
                    variant="standard"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') doGlobalSearch();
                    }}
                    InputProps={{
                      ...InputProps,
                      disableUnderline: true,
                      startAdornment: (
                        <Box sx={{ pl: 1.5, pr: 1, display: 'flex', alignItems: 'center', color: 'inherit' }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                      endAdornment: (
                        <>
                          {globalSearching ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                          {InputProps.endAdornment}
                        </>
                      ),
                      sx: { color: 'inherit', py: 0.5 },
                    }}
                  />
                );
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton color="inherit" onClick={() => navigate('/')} title="Home">
            <HomeIcon />
          </IconButton>

          <IconButton color="inherit" onClick={handleNotificationsClick}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleMenuOpen}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#1A5490', mr: 1 }}>
              {user?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' }, mr: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {user?.name}
              </Typography>
              <Chip label={user?.role} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
